import { Controller } from "@hotwired/stimulus"
import {
  STATUS_OUTCOMES, clearUnavailable, hasConflictingIdentity,
  showUnavailable, validateStatus
} from "../lib/upload_status_feedback"
import { applyStatusFragment, parseStatusFragment } from "../lib/upload_status_fragment"

const POLL_DELAYS = [2000, 4000, 8000, 15000]
const TERMINAL_STATES = new Set([
  "ready",
  "rejected",
  "failed",
  "cancelled",
  "superseded",
  "expired"
])

// Polls each application upload through its own signed #3400 status URL. This
// intentionally does not subscribe to Cable or widen the authenticated batch
// endpoint: staff can see the server-rendered row while only the exact applicant
// receives live, per-item updates.
export default class extends Controller {
  connect() {
    this.pollDelayIndex = 0
    this.pollTimer = null
    this.pollInFlight = false
    this.visibilityEvent = () => this.visibilityChanged()
    this.onlineEvent = () => this.onlineChanged()
    document.addEventListener("visibilitychange", this.visibilityEvent)
    window.addEventListener("online", this.onlineEvent)
    window.addEventListener("offline", this.onlineEvent)
    this.poll()
  }

  disconnect() {
    this.cancelPoll()
    document.removeEventListener("visibilitychange", this.visibilityEvent)
    window.removeEventListener("online", this.onlineEvent)
    window.removeEventListener("offline", this.onlineEvent)
  }

  async poll() {
    const items = this.activeItems()
    if (!this.canPoll() || this.pollInFlight || items.length === 0) return

    this.pollInFlight = true
    try {
      // pollItem converts each failure into a row outcome, so a fast failure
      // cannot release pollInFlight while another row's request is outstanding.
      const outcomes = await Promise.all(items.map((item) => this.pollItem(item)))
      this.pollDelayIndex = outcomes.includes(STATUS_OUTCOMES.unavailable)
        ? Math.min(this.pollDelayIndex + 1, POLL_DELAYS.length - 1)
        : 0
    } finally {
      this.pollInFlight = false
      this.schedulePoll()
    }
  }

  async pollItem(item) {
    try {
      const response = await fetch(item.dataset.uploadStatusUrl, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Upload-Fragment": "true" }
      })
      if (!response.ok) throw new Error("Upload status request failed")

      const payload = await response.json()
      if (!this.activeItems().includes(item)) return STATUS_OUTCOMES.ignored
      return this.updateItem(item, payload)
    } catch (_error) {
      return this.activeItems().includes(item)
        ? showUnavailable(item)
        : STATUS_OUTCOMES.ignored
    }
  }

  updateItem(item, payload) {
    if (hasConflictingIdentity(payload, item)) return STATUS_OUTCOMES.ignored
    const validated = validateStatus(this.element, item, payload)
    if (!validated) return showUnavailable(item)
    const { incomingTime, currentTime } = validated
    if (incomingTime < currentTime) return STATUS_OUTCOMES.ignored
    if (incomingTime === currentTime && item.dataset.uploadState !== payload.state) return STATUS_OUTCOMES.ignored
    const fragment = parseStatusFragment(item, payload)
    if (!fragment) return showUnavailable(item)

    clearUnavailable(item)
    applyStatusFragment(item, fragment)
    item.dataset.uploadState = payload.state
    if (typeof payload.published === "boolean") {
      item.dataset.uploadPublished = String(payload.published)
    }
    return STATUS_OUTCOMES.applied
  }

  schedulePoll() {
    if (!this.canPoll() || this.pollTimer || this.activeItems().length === 0) return

    const delay = POLL_DELAYS[Math.min(this.pollDelayIndex, POLL_DELAYS.length - 1)]
    this.pollDelayIndex = Math.min(this.pollDelayIndex + 1, POLL_DELAYS.length - 1)
    this.pollTimer = window.setTimeout(() => {
      this.pollTimer = null
      this.poll()
    }, delay)
  }

  cancelPoll() {
    if (this.pollTimer) window.clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  visibilityChanged() {
    if (document.hidden) {
      this.cancelPoll()
    } else {
      this.poll()
    }
  }

  onlineChanged() {
    if (!navigator.onLine) {
      this.cancelPoll()
    } else {
      this.poll()
    }
  }

  canPoll() {
    return !document.hidden && navigator.onLine !== false
  }

  activeItems() {
    return Array.from(this.element.querySelectorAll("[data-upload-status-url]")).filter(
      (item) => !TERMINAL_STATES.has(item.dataset.uploadState)
    )
  }
}
